import React, { useContext, useEffect, useState, } from 'react';
import { Context } from './Context';

import styles from './styles/bim.module.scss';
import { useTranslation } from 'react-i18next';

import LangSwitcher from './components/LangSwitcher';
import Loading from './components/Loading'
import VisualizationView from './components/VisualizationView'
import ModelView from "./components/ModelView"
import TiersView from './components/TiersView';
import ApartmentsView from './components/ApartmentsView';
import ApartmentView from "./components/ApartmentView/ApartmentView";
import ApartmentViewContextProvider from "./components/ApartmentView/context/ApartmentViewContext";

import MobileNavBar from './components/MobileNavBar';
import LocalizationView from './components/LocalizationView';
import i18n from 'i18next';
import FavoriteApartments from './components/FavoriteApartments/FavoriteApartments';
import {MOBILE_VIEW_TYPES} from './constants/mobileViewTypes';
import ApartmentsFilter from './components/ApartmentsFilter';

const { MODEL_VIEW, TIERS_VIEW, VISUALIZATIONS_VIEW, LOCALIZATION_VIEW, APARTMENT_VIEW, FAVORITES_VIEW} = MOBILE_VIEW_TYPES;

function App() {

  const { t } = useTranslation();
  const { 
    projectStyle, 
    isDesktop, 
    isMobile, 
    loading, 
    mobileActiveView, 
    activeTier, 
    setActiveTier, 
    showView, 
    setShowView,
    projectSetup, 
  } = useContext(Context);

  const isModelViewSection = projectSetup?.website?.sections?.render_360; // && (projectSetup?.site?.render_360 ||  projectSetup?.buildings?.render_360);
  const isModelView = (projectSetup?.site?.render_360 || projectSetup?.buildings?.render_360) || false;
  const isTiersViewSection = projectSetup?.website?.sections?.tiers;
  // const isModelViewSection = projectSetup?.site?.render_360 ||  projectSetup?.buildings?.render_360;

  const [activeApartment, setActiveApartment] = useState(null)

  const defaultFonts = {
    font_url_default: 'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&family=Poppins:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap',
    font_primary_default: 'Lato, sans-serif',
    font_secondary_default: 'Poppins, sans-serif',
    font_headings_default: 'Playfair Display, serif',
  };

  const ensureHexColor = (color) => {
    return color.startsWith('#') ? color : `#${color}`;
  };


  useEffect(() => {
    const linkId = 'app-dynamic-fonts';
    let link = document.getElementById(linkId);

    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    const newHref = (projectStyle && projectStyle.__font_url) 
      ? projectStyle.__font_url
      : defaultFonts.font_url_default;

    if (link.href !== newHref) {
      link.href = newHref;
    }
  }, [projectStyle]);

  useEffect(() => {
    // If running as WordPress plugin, use WP language setting
    // Otherwise, language is already set from app-config in i18n.js
    if (window.bimWebloData?.current_language) {
      const wpLanguage = window.bimWebloData.current_language;
      // Convert 'pl_PL' to 'pl', 'en_US' to 'en', etc.
      const shortLang = wpLanguage.split('_')[0];
      i18n.changeLanguage(shortLang);
      console.log('Language set from WordPress:', shortLang);
    } else {
      console.log('Language already set from app-config:', i18n.language);
    }
  }, []);

  return (
    <div className={styles.app} data-app="bim_cadaplus">
     
      {projectStyle && (
        <style>
          {`:root {
          ${projectStyle.__primary_color ? `--primary-color:#${projectStyle.__primary_color};` : '--primary-color:#aba49c;'}
          ${projectStyle.__secondary_color ? `--secondary-color:#${projectStyle.__secondary_color};` : '--secondary-color:#aba49c;'}
          ${projectStyle.__dark_color ? `--dark-color:#${projectStyle.__dark_color};` : '--dark-color:#000000;'}
          ${projectStyle.__light_color ? `--light-color:#${projectStyle.__light_color};` : '--light-color:#d2d2d2;'}
          ${projectStyle.__gray_color ? `--gray-color:#${projectStyle.__gray_color};` : '--gray-color:#aba49c;'}
          ${projectStyle.__white_color ? `--white-color:#${projectStyle.__white_color};` : '--white-color:#ffffff;'}
          ${projectStyle.__accent_color ? `--accent-color:#${projectStyle.__accent_color};` : '--accent-color:#aba49c;'}
          ${projectStyle.__headings_color ? `--headings-color:#${projectStyle.__headings_color};` : '--headings-color:#000000;'}
          
            --font-primary: ${projectStyle.__font_primary_family || defaultFonts.font_primary_default};
            --font-secondary: ${projectStyle.__font_secondary_family || defaultFonts.font_secondary_default};
            --font-headings: ${projectStyle.__font_headings_family || defaultFonts.font_headings_default};

            --color-primary:${ensureHexColor(projectStyle.__color_primary || '#DCC69B')};
            --color-primary-hover:${ensureHexColor(projectStyle.__color_primary_hover || '#DCC69B')};
            --color-primary-highlight:${ensureHexColor(projectStyle.__color_primary_highlight || '#275F57')};
            --color-on-primary:${ensureHexColor(projectStyle.__color_on_primary || '#DCC69B')};
            --color-on-primary-low-contrast:${ensureHexColor(projectStyle.__color_on_secondary_low_contrast || '#4b4b4b')};
            --color-secondary:${ensureHexColor(projectStyle.__color_secondary || '#DCD09B')};
            --color-secondary-hover:${ensureHexColor(projectStyle.__color_secondary_hover || '#DCD09B')};
            --color-on-secondary:${ensureHexColor(projectStyle.__color_on_secondary || '#DCD09B')};
            --color-on-secondary-low-contrast:${ensureHexColor(projectStyle.__color_on_secondary_low_contrast || '#4b4b4b')};
            --color-accent:${ensureHexColor(projectStyle.__color_accent || '#EAB6A0')};
            --color-accent-hover:${ensureHexColor(projectStyle.__color_accent_hover || '#EAB6A0')};
            --color-on-accent:${ensureHexColor(projectStyle.__color_on_accent || '#EAB6A0')};
            --color-on-accent-low-contrast:${ensureHexColor(projectStyle.__color_on_accent_low_contrast || '#4b4b4b')};
            --color-headings:${ensureHexColor(projectStyle.__color_headings || '#EAC3A0')};
            --color-labels:${ensureHexColor(projectStyle.__color_labels || '#EAC3A0')};
            --color-data:${ensureHexColor(projectStyle.__color_data || '#EAC3A0')};
            --color-white:${ensureHexColor(projectStyle.__color_white || '#ffffff')};
            --color-offwhite:${ensureHexColor(projectStyle.__color_offwhite || '#D0D0D0')};
            --color-light:${ensureHexColor(projectStyle.__color_light || '#B9C5E0')};
            --color-dark:${ensureHexColor(projectStyle.__color_dark || '#A6B4D3')};
            --color-emphasis:${ensureHexColor(projectStyle.__color_emphasis || '#A1AFCE')};

            --section-padding: ${projectStyle.__section_padding || '4rem'}
          }

            .aprt .rm{
              opacity:0.4 !important;
            }
            
            ${projectStyle.__state_available ? `.state-available{color:#${projectStyle.__state_available} !important}` : ""}
            ${projectStyle.__state_reserved ? `.state-reserved{color:#${projectStyle.__state_reserved} !important}` : ""}
            ${projectStyle.__state_sold ? `.state-sell{color:#${projectStyle.__state_sold} !important}` : ""}

        `}
        </style>

      )}
      {projectStyle && (
        <style>

          {/* {Object.entries(projectStyle).filter(([key]) => key.endsWith('_color') && !key.startsWith('__')).map(([key, value]) => {
            const className = key.replace('_color', '');
            return value ? `.${className} {
              fill: #${value};
            }` : '';
          }).join('\n          ')} */}

          {projectStyle.roomsColors && projectStyle.roomsColors.map((color, index) => {
            return (`
              svg *[data-rooms="${index}"].rooms-color {
                  opacity:1 !important;
                  fill: #${color}60 !important;
                  stroke: #${color} !important;
                  stroke-width: 2px !important;
                }
                svg *[data-rooms="${index}"].rooms-color:hover {
                  opacity:1 !important;
                  fill: #${color}ab !important;
                  stroke: #${color} !important;
                  stroke-width: 2px !important;
                }
                svg *.active_filter[data-rooms="${index}"].rooms-color {
                  fill: #${color}95 !important;
                  opacity:1 !important;
                  stroke: #${color} !important;
                  stroke-width: 2px !important;
                }
        `)
          })}
          
          {projectStyle.__state_sold &&
            `svg .state-sell{
              opacity: 1 !important;
              fill:#${projectStyle.__state_sold}60 !important; 
              stroke:#${projectStyle.__state_sold} !important; 
              stroke-width: 2px !important;
            }
              
            svg .state-sell:hover{
              fill:#${projectStyle.__state_sold}60 !important;
              cursor: default !important;
            }
          `}   
        </style>)}

      {/* {<LangSwitcher />} */}
      {loading ? (
        <Loading />
      ) : (
        <>
          <main style={{paddingBottom: '4rem'}}>
            {isModelViewSection && (
              <div className={styles.visualization_container}>
              {(isDesktop && (isModelView && showView >= 1)) && (
                  <ModelView setShowView={setShowView} />
                )}
                {(isDesktop && showView == 0) && (
                  <VisualizationView setShowView={setShowView} setActiveTier={setActiveTier} />
                )}
                {(isMobile && mobileActiveView == MODEL_VIEW) && (
                  <ModelView setShowView={setShowView} />
                )}
                {(isMobile && mobileActiveView == VISUALIZATIONS_VIEW) && (
                  <VisualizationView setShowView={setShowView} setActiveTier={setActiveTier} />
                )}
              </div>
            )}
            
              {/* {(mobileActiveView == MODEL_VIEW || mobileActiveView == VISUALIZATIONS_VIEW) && (
                <>
                <div className={styles.visualization_container}>
                  {((isDesktop && showView == 0) || (isMobile && mobileActiveView == VISUALIZATIONS_VIEW)) && (
                    <VisualizationView setShowView={setShowView} setActiveTier={setActiveTier} />
                  )}
                  {(isModelView && ((isDesktop && showView >= 1) || (isMobile && mobileActiveView == MODEL_VIEW))) && (
                    <>
                      <ModelView setShowView={setShowView} />
                    </>
                  )}
                </div>
              </>
              )}
               */}
            
            {(isTiersViewSection && isMobile && mobileActiveView == TIERS_VIEW) && <TiersView />}
            {isMobile && mobileActiveView == APARTMENT_VIEW &&(
              <>
              <ApartmentViewContextProvider>
                <ApartmentView />
              </ApartmentViewContextProvider>
              </>
            )}
            {isMobile && mobileActiveView == FAVORITES_VIEW &&(
              <FavoriteApartments />
            )}
            {(isDesktop || 
              (isMobile && mobileActiveView == MODEL_VIEW) || 
              (isMobile && mobileActiveView == TIERS_VIEW) || 
              (isMobile && mobileActiveView == VISUALIZATIONS_VIEW) || 
              (isMobile && mobileActiveView == FAVORITES_VIEW)) && 
              <>
                <ApartmentsFilter />
                <ApartmentsView />
              </>
            }
            {(isTiersViewSection && isDesktop && activeTier != null) ? <TiersView /> : null}
            {(isMobile && mobileActiveView == LOCALIZATION_VIEW) && <LocalizationView />}
            {/* {isDesktop && <LocalizationView />} */}
            {isDesktop && (
              <>
              <ApartmentViewContextProvider>
                <ApartmentView />
              </ApartmentViewContextProvider>
              <FavoriteApartments />
              </>
            )}
          </main>
        </>
      )}
      {isMobile && <MobileNavBar />}
    </div>
  );
}

export default App;
